import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { adminLogin, setAdminToken, getAdminToken } from "@/lib/api";
import { Container } from "@/components/Sections";
import { ArrowRight, Lock } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (getAdminToken()) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Password required");
      return;
    }
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      setAdminToken(token);
      toast.success("Signed in");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="bg-[#F6F1E6] min-h-screen">
      <section className="pt-40 md:pt-52 pb-20">
        <Container>
          <div className="max-w-md">
            <Lock className="text-[#C45A3C] mb-8" size={28} />
            <p className="overline mb-4">Admin</p>
            <h1
              data-testid="admin-login-title"
              className="font-serif text-5xl md:text-6xl leading-[1.02] mb-10 text-[#1F2A1B]"
            >
              Sign in to the
              <br />
              <span className="italic text-[#3F5132]">dashboard.</span>
            </h1>

            <form
              onSubmit={onSubmit}
              data-testid="admin-login-form"
              className="space-y-8"
            >
              <input
                type="password"
                required
                autoFocus
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input"
                data-testid="admin-login-password"
              />

              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="btn-primary disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign in"}
                <ArrowRight size={16} />
              </button>
            </form>

            <p className="text-[#8A8775] text-xs mt-12 leading-relaxed">
              Single-administrator access. Sessions expire after 60 minutes.
            </p>
          </div>
        </Container>
      </section>
    </div>
  );
}
